import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Appointment, Chair, VisitSummary } from "@dentacore/shared-types";

import { statusLabel } from "../calendar/status-graph";
import { listVisits } from "../api/patients";
import { clockOf, longDate, relativeTo, today, upNext } from "./home-data";
import styles from "./home.module.css";

/**
 * **The next patient, first** — the box at the top of Home's left column
 * (decision CS2).
 *
 * **Up next briefs the clinician before the patient sits down** — the mockup's
 * point, and why the facts under the name are the patient's own flags rather
 * than a summary. They arrive with the booking, so the box costs no extra
 * request beyond the last visit.
 *
 * It holds what *Up next* always held — time, how soon, chair, name, what for,
 * flags, last visit, Open file and the status — and nothing new: the status is
 * changed from the patient's row in *Today* below, so a second control here
 * would be two places doing one job.
 *
 * *Watch today* used to sit under this. Its rows are chips on each patient's
 * row in *Today* now (CS3): it only ever covered patients still to be seen,
 * which is exactly that list.
 */
export function UpNext({
  appointments,
  chairs,
  timezone,
}: {
  appointments: readonly Appointment[] | null;
  chairs: readonly Chair[];
  timezone: string;
}): React.JSX.Element {
  const next = upNext(appointments ?? []);

  /**
   * The last time this patient was in — decision K3.
   *
   * **The most recent date before today**, not simply the newest. `listVisits`
   * returns every date they have records on including today, so a patient with
   * a record from earlier today would be briefed with "Last visit: today",
   * which tells a clinician nothing they do not already know.
   *
   * `undefined` is "still looking", `null` is "there is no earlier date" — which
   * renders **First visit**, a genuinely useful clinical fact rather than a
   * blank. Fetched per patient, because the day's appointments do not carry it
   * and a dashboard cannot ask for every patient's history.
   */
  const [lastVisit, setLastVisit] = useState<VisitSummary | null | undefined>(undefined);
  const patientId = next?.patient?.id ?? null;

  useEffect(() => {
    if (patientId === null) {
      setLastVisit(null);
      return;
    }

    const controller = new AbortController();
    setLastVisit(undefined);

    listVisits(patientId, controller.signal)
      .then(({ visits }) => {
        // Newest first, so the first one before today is the one wanted.
        const day = today(timezone);
        setLastVisit(visits.find((visit) => visit.date < day) ?? null);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        // Silent, and read as a first visit rather than an error: a brief that
        // could not reach one endpoint must not put a failure in front of a
        // clinician who is about to see a patient.
        setLastVisit(null);
      });

    return () => controller.abort();
  }, [patientId, timezone]);

  const chairName = (chairId: string): string =>
    chairs.find((chair) => chair.id === chairId)?.name ?? "—";

  if (appointments === null) {
    return (
      <section className={styles.next} data-testid="up-next">
        <p className={styles.quiet}>Reading&hellip;</p>
      </section>
    );
  }

  if (next === null) {
    return (
      <section className={styles.next} data-testid="up-next">
        <p className={styles.quiet} data-testid="up-next-empty">
          Nobody else is booked today.
        </p>
      </section>
    );
  }

  return (
    <section
      className={styles.next}
      data-testid="up-next"
      aria-label="Up next"
      // The same visit as its row in Today, to the quick ring (STORY-221).
      data-ring="home.visit"
      data-id={next.id}
    >
      <div className={styles.nextWhen}>
        <span className={styles.nextClock}>{clockOf(next.startsAt, timezone)}</span>
        <span className={styles.nextRelative}>{relativeTo(next.startsAt)}</span>
      </div>

      <div className={styles.nextWho}>
        <p className={styles.nextEyebrow}>Up next &middot; {chairName(next.chairId)}</p>
        <p className={styles.nextName} data-testid="up-next-name">
          {next.patient?.firstName} {next.patient?.lastName}
        </p>
        <p className={styles.nextProcedure}>{next.procedureSummary ?? "No procedure noted"}</p>

        {(next.patient?.flags ?? []).length > 0 && (
          <ul className={styles.facts}>
            {(next.patient?.flags ?? []).map((flag) => (
              <li
                key={flag.id}
                className={flag.severity === "CRITICAL" ? styles.flagCritical : styles.flag}
              >
                {flag.label}
                {flag.detail !== null && ` — ${flag.detail}`}
              </li>
            ))}
          </ul>
        )}

        <p className={styles.lastVisit} data-testid="up-next-last-visit">
          {lastVisit === undefined
            ? "Looking up their last visit…"
            : lastVisit === null
              ? "First visit"
              : `Last visit ${longDate(lastVisit.date)}${
                  lastVisit.title === null ? "" : ` — ${lastVisit.title}`
                }${lastVisit.providerName === null ? "" : ` · ${lastVisit.providerName}`}`}
        </p>
      </div>

      <div className={styles.nextActions}>
        <Link to={`/patients/${next.patient?.id ?? ""}`} className={styles.ghost}>
          Open file
        </Link>
        <span className={styles.nextStatus}>{statusLabel(next.status)}</span>
      </div>
    </section>
  );
}
